import t from 'react-translate';
import { css } from '@emotion/react';
import {
  PAGE_SIZE,
  PageSizeParams,
} from 'athena/components/mentoring-program/constants';
import { useEffect, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useInView } from 'react-intersection-observer';
import {
  MentorQuestionAnswerFilter,
  searchMentors,
} from 'redux/actions/mentoring-program-participant-driven';
import {
  MentoringProgramEnrollment,
  MentoringProgramRole,
} from 'redux/schemas/models/mentoring-program-enrollments';
import {
  selectMentorsList,
  selectMentorsResultsCount,
} from 'redux/selectors/mentorship-program-participant-driven';
import { useAppDispatch } from 'redux/store';
import { usePaginatedApi } from 'shared/hooks/use-paginated-api';
import {
  standardSpacing,
  tripleSpacing,
} from 'styles/global_defaults/scaffolding';
import MentorCard, { CARD_WIDTH } from './mentor-card';
import { useSelector } from 'react-redux';
import { textMediumLineHeight } from 'styles/global_defaults/fonts';
import { AllLoaded } from 'lhs/components/loading-placeholders';
import LoadingPlaceholders from './loading-placeholders';
import { Button } from 'react-bootstrap';
import NvNoResults from 'shared/components/nv-no-results-panel';
import EmptyParticipants from 'athena/components/mentoring-program/participants/empty-participants';
import { config } from '@config/pendo.config.json';
import { getMentoringProgramAliases } from 'redux/selectors/mentoring-programs';

type BrowseMentorsContentProps = {
  programId: number;
  onScroll?: React.UIEventHandler<HTMLDivElement>;
  clearFilters?: () => void;
};

const emptyMentorsContentStyles = css`
  margin-top: 205px;
  svg {
    width: 100px;
    height: 100px;
  }
`;

const browseMentorsContentStyles = css`
  flex-grow: 1;
  overflow-y: auto;
  min-height: 0;
  padding: 0 ${standardSpacing}px;

  .mentors-list-results {
    padding-top: ${standardSpacing}px;
    line-height: ${textMediumLineHeight}px;
  }

  .mentors-list {
    display: grid;
    grid-template-columns: repeat(auto-fit, ${CARD_WIDTH}px);
    gap: ${standardSpacing}px;
  }

  .mentors-filters-clear-all {
    justify-self: center;
  }

  .all-loaded {
    text-align: center;
  }
`;

const BrowseMentorsContent = ({
  programId,
  onScroll,
  clearFilters,
}: BrowseMentorsContentProps) => {
  const { watch, setValue } = useFormContext();
  const searchTerm = watch('search');
  const areFiltersSet = watch('areFiltersSet');
  const filters = watch('filters') || {};
  const dispatch = useAppDispatch();
  const mentoringProgramAliases = useSelector((state) => getMentoringProgramAliases(state, programId));
  const { ref: endRef, inView } = useInView();
  const resultsCount = useSelector(selectMentorsResultsCount);
  const translationBase =
    t.MENTORING_PROGRAMS.PARTICIPANT_HOME.GET_STARTED.FIND_A_MENTOR_MODAL
      .CONTENT;

  const formattedFilters = useMemo(
    () =>
      Object.entries(filters)
        .filter(([_, answers]) => Array.isArray(answers) && answers.length > 0)
        .map(([question_id, answers]) => ({
          question_id: Number(question_id),
          answers: answers as string[],
        })),
    [filters]
  );

  const [appliedFilters, setAppliedFilters] = useState<
    MentorQuestionAnswerFilter[]
  >(formattedFilters);

  useEffect(() => {
    if (areFiltersSet) {
      setAppliedFilters(formattedFilters);
      setValue('areFiltersSet', false);
    }
  }, [areFiltersSet, formattedFilters, setValue]);

  const params = useMemo(
    () => ({
      pageSize: PAGE_SIZE,
      searchTerm,
      filters: appliedFilters,
    }),
    [searchTerm, appliedFilters]
  );

  const { result, loadMore, isLoading, allLoaded, page } = usePaginatedApi<
    MentoringProgramEnrollment,
    PageSizeParams
  >(
    (p) =>
      dispatch(
        searchMentors({
          programId,
          searchTerm: params.searchTerm,
          filters: params.filters,
          ...p,
        })
      ).then((action) => action.payload),
    params,
    selectMentorsList
  );

  useEffect(() => {
    if (inView && !isLoading && result?.length) {
      loadMore();
    }
  }, [loadMore, inView]);

  const hasActiveSearchOrFilters = !!searchTerm || !!appliedFilters.length;

  const clearSearchAndFilters = () => {
    setValue('search', '', { shouldDirty: true, shouldValidate: true });
    clearFilters?.();
    setAppliedFilters([]);
  };

  if (allLoaded && !isLoading && !result?.length) {
    if (hasActiveSearchOrFilters) {
      return (
        <NvNoResults
          action={clearSearchAndFilters}
          noResultsTextComponent={
            <span className='text-xl light' aria-live='assertive'>
              {t.SEARCH.NO_RESULTS_FOUND()}
            </span>
          }
          clearText={t.SEARCH.CLEAR_ALL()}
        />
      );
    }
    return (
      <div css={emptyMentorsContentStyles}>
        <EmptyParticipants
          className='text-align-center'
          role={MentoringProgramRole.MENTORSHIP_PROGRAM_MENTOR}
          label={translationBase.EMPTY(mentoringProgramAliases)}
        />
      </div>
    );
  }

  const dataQATag =
    config.pendo.athena.mentorshipProgram.participantHome.findAMentor.modal;

  return (
    <div css={browseMentorsContentStyles} onScroll={onScroll}>
      {!!result?.length ? (
        <>
          <div className='mentors-list-results text-xl font-weight-light gray-1 text-align-center'>
            {!isLoading && translationBase.RESULTS_COUNT(resultsCount)}
          </div>
          {hasActiveSearchOrFilters && (
            <Button
              variant='link'
              onClick={clearSearchAndFilters}
              className='mentors-filters-clear-all pt-1 pb-0 d-flex border-0'
              data-qa={dataQATag.clearSearchAndFilters}
            >
              {t.SEARCH.CLEAR_ALL()}
            </Button>
          )}
        </>
      ) : (
        <div className='mentors-list-results text-medium text-align-center'>
          {isLoading && translationBase.LOADING_RESULTS()}
        </div>
      )}
      <ul className='mentors-list pt-4 list-unstyled justify-content-center'>
        {!(isLoading && page === 1) && result?.map((mentor) => (
          <li key={mentor.id}>
            <MentorCard mentor={mentor} />
          </li>
        ))}
        {isLoading && <LoadingPlaceholders />}
      </ul>
      <div ref={endRef} />
      {!isLoading && allLoaded && !!result?.length && <AllLoaded />}
    </div>
  );
};

export default BrowseMentorsContent;
